import type { RouteStatus } from '../../types';

export type RoutesPageExecutionFilter = 'all' | 'own' | 'carriers';

export type RoutesPagePreset =
  | 'all'
  | 'await_loading'
  | 'in_transit'
  | 'await_payment'
  | 'completed'
  | 'issues'
  | 'ready_for_completion';

export type RoutesPagePaymentFilter =
  | 'all'
  | 'ok'
  | 'partial'
  | 'open'
  | 'overdue'
  | 'await_payment';

export type RouteListScope = 'active' | 'archive';

export type RoutesPagePersistedFilters = {
  preset: RoutesPagePreset;
  paymentFilter: RoutesPagePaymentFilter;
  search?: string;
  transportType?: string;
  status?: RouteStatus;
  applicationId?: number;
  executionScope?: RoutesPageExecutionFilter;
  responsibleLogistId?: number;
  carrierId?: number;
  clientId?: number;
  driverId?: number;
  vehicleId?: number;
  dateFrom?: string;
  dateTo?: string;
  listScope: RouteListScope;
  /** @deprecated migrated to listScope */
  showCompletedRoutes?: boolean;
  page?: number;
  limit?: number;
};

export const ROUTES_PAGE_FILTERS_SESSION_KEY = 'routes_page_filters_v1';

export const DEFAULT_ROUTES_PAGE_FILTERS: RoutesPagePersistedFilters = {
  preset: 'all',
  paymentFilter: 'all',
  listScope: 'active',
  page: 1,
  limit: 20,
  executionScope: 'all',
};

export function loadRoutesPageFiltersFromSession(): Partial<RoutesPagePersistedFilters> | null {
  if (typeof window === 'undefined' || !window.sessionStorage) return null;
  const raw = window.sessionStorage.getItem(ROUTES_PAGE_FILTERS_SESSION_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as Partial<RoutesPagePersistedFilters>;
  } catch {
    return null;
  }
}

export function saveRoutesPageFiltersToSession(state: RoutesPagePersistedFilters): void {
  if (typeof window === 'undefined' || !window.sessionStorage) return;
  window.sessionStorage.setItem(ROUTES_PAGE_FILTERS_SESSION_KEY, JSON.stringify(state));
}

export function mergeRoutesPageFilters(
  saved: Partial<RoutesPagePersistedFilters> | null | undefined
): RoutesPagePersistedFilters {
  if (!saved) return { ...DEFAULT_ROUTES_PAGE_FILTERS };
  return {
    ...DEFAULT_ROUTES_PAGE_FILTERS,
    ...saved,
    preset: saved.preset ?? DEFAULT_ROUTES_PAGE_FILTERS.preset,
    paymentFilter: saved.paymentFilter ?? DEFAULT_ROUTES_PAGE_FILTERS.paymentFilter,
    listScope:
      saved.listScope === 'archive' || saved.listScope === 'active'
        ? saved.listScope
        : saved.showCompletedRoutes === true
          ? 'archive'
          : saved.showCompletedRoutes === false
            ? 'active'
            : DEFAULT_ROUTES_PAGE_FILTERS.listScope,
  };
}
